import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, RefreshCw, Heart, CircleDollarSign, ThumbsUp, Share2, Undo2, Flame, Bookmark, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORY_COLORS = {
  "AI-Powered": "bg-purple-500/90",
  FinTech: "bg-blue-500/90",
  HealthTech: "bg-emerald-500/90",
  CleanTech: "bg-teal-500/90",
  SaaS: "bg-orange-500/90",
  AgriTech: "bg-lime-600/90",
  PropTech: "bg-rose-500/90",
};

export const PitchFlipCard = ({ pitch }) => {
  const [flipped, setFlipped] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [counts, setCounts] = useState({ hearts: pitch.hearts, dollars: pitch.dollars, likes: pitch.likes, shares: pitch.shares });
  const [reacted, setReacted] = useState({});
  const videoRef = useRef(null);
  const { user, savedIds, toggleSave } = useAuth();
  const isSaved = savedIds.has(pitch.id);

  const onBookmark = async () => {
    try {
      const saved = await toggleSave(pitch.id);
      toast.success(saved ? `${pitch.company} added to your watchlist` : `${pitch.company} removed from watchlist`);
    } catch (e) {
      toast.error("Could not update watchlist. Please try again.");
    }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {
        setPlaying(false);
        toast.error("This video is unavailable right now. Please try again later.");
      });
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const engage = async (type, field) => {
    if (reacted[type]) return;
    setReacted((r) => ({ ...r, [type]: true }));
    setCounts((c) => ({ ...c, [field]: c[field] + 1 }));
    try { await api.post(`/pitches/${pitch.id}/engage`, { type }); } catch (e) { /* silent */ }
  };

  const share = async () => {
    engage("share", "shares");
    try { await navigator.clipboard.writeText(`${window.location.origin}/pitch/${pitch.id}`); } catch (e) { /* clipboard blocked */ }
    toast.success(`Link to ${pitch.company}'s pitch copied!`);
  };

  return (
    <div data-testid={`pitch-card-${pitch.id}`} className="w-full">
      <div className="flip-container w-full aspect-[16/12]">
        <div className={`flip-inner ${flipped ? "flipped" : ""}`}>
          {/* FRONT */}
          <div className="flip-face bg-[#121212] border border-white/10 flex flex-col">
            <div className="relative flex-1 bg-black overflow-hidden group">
              <video
                ref={videoRef}
                src={pitch.video_url}
                poster={pitch.thumbnail}
                className="w-full h-full object-cover"
                onEnded={() => setPlaying(false)}
                onError={() => setPlaying(false)}
                playsInline
                preload="none"
              />
              <span className={`absolute top-3 left-3 text-[10px] font-bold text-white px-2 py-1 rounded ${CATEGORY_COLORS[pitch.category] || "bg-zinc-600"}`}>
                {pitch.category}
              </span>
              <span className="absolute bottom-3 right-3 text-[10px] font-semibold text-white bg-black/70 px-2 py-0.5 rounded">{pitch.duration}</span>
              {pitch.trending && (
                <span
                  data-testid={`pitch-trending-badge-${pitch.id}`}
                  title={`Hot streak: +${pitch.weekly_hearts} hearts, +${pitch.weekly_dollars} dollars this week`}
                  className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-bold text-black px-2 py-1 rounded-full bg-gradient-to-r from-[#F5B301] to-orange-500 trending-badge"
                >
                  <Flame size={11} fill="black" /> Trending
                </span>
              )}
              {!playing && (
                <button
                  data-testid={`pitch-play-btn-${pitch.id}`}
                  onClick={togglePlay}
                  aria-label="Play pitch video"
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center transition-transform group-hover:scale-110">
                    <Play size={18} className="text-white ml-0.5" fill="white" />
                  </span>
                </button>
              )}
              {playing && (
                <button onClick={togglePlay} aria-label="Pause" className="absolute bottom-3 left-3 p-1.5 rounded-full bg-black/60 text-white">
                  <Pause size={14} />
                </button>
              )}
            </div>
            <div className="p-3.5 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/pitch/${pitch.id}`} data-testid={`pitch-title-link-${pitch.id}`} className="font-heading font-semibold text-white text-sm truncate block hover:text-[#F5B301] transition-colors">
                  {pitch.company}
                </Link>
                <p className="text-xs text-zinc-500 truncate">{pitch.sector}</p>
              </div>
              <button
                data-testid={`pitch-flip-btn-${pitch.id}`}
                onClick={() => setFlipped(true)}
                className="shrink-0 flex items-center gap-1.5 text-[11px] font-semibold text-[#F5B301] border border-[#F5B301]/40 rounded-full px-3 py-1.5 hover:bg-[#F5B301]/10 transition-colors"
              >
                <RefreshCw size={11} /> Flip to Deck
              </button>
              <Link
                to={`/pitch/${pitch.id}`}
                data-testid={`pitch-open-btn-${pitch.id}`}
                title="Open full pitch page"
                className="shrink-0 flex items-center gap-1 text-[11px] font-bold text-black bg-[#F5B301] rounded-full px-3 py-1.5 hover:bg-[#D99E01] transition-colors"
              >
                Open <ArrowUpRight size={11} />
              </Link>
            </div>
          </div>
          {/* BACK */}
          <div className="flip-face flip-back bg-[#141414] border border-[#F5B301]/25 p-4 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <div>
                <Link to={`/pitch/${pitch.id}`} data-testid={`pitch-back-title-link-${pitch.id}`} className="font-heading font-bold text-white text-base hover:text-[#F5B301] transition-colors">
                  {pitch.company} →
                </Link>
                <p className="text-[11px] text-zinc-500">{pitch.founder} · {pitch.founder_title}</p>
              </div>
              <span className="text-[#F5B301] font-heading font-bold text-lg">{pitch.score}</span>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 text-left hide-scrollbar">
              {[["Problem", pitch.deck.problem], ["Solution", pitch.deck.solution], ["Market", pitch.deck.market], ["Traction", pitch.deck.traction], ["Use of Funds", pitch.deck.use_of_funds]].map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-widest text-[#F5B301] font-semibold mb-0.5">{k}</p>
                  <p className="text-xs text-zinc-300 leading-relaxed">{v}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between gap-2">
              <div className="text-left min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Raising</p>
                <p className="text-sm font-heading font-bold text-white truncate">{pitch.deck.ask}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={`/pitch/${pitch.id}`}
                  data-testid={`pitch-fullpage-btn-${pitch.id}`}
                  className="flex items-center gap-1 text-[11px] font-semibold text-[#F5B301] border border-[#F5B301]/40 rounded-full px-3 py-1.5 hover:bg-[#F5B301]/10 transition-colors"
                >
                  Full Pitch <ArrowUpRight size={11} />
                </Link>
                <button
                  data-testid={`pitch-flipback-btn-${pitch.id}`}
                  onClick={() => setFlipped(false)}
                  className="flex items-center gap-1.5 text-[11px] font-semibold text-black bg-[#F5B301] rounded-full px-3 py-1.5 hover:bg-[#D99E01] transition-colors"
                >
                  <Undo2 size={11} /> Video
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Engagement bar */}
      <div className="mt-2.5 flex items-center justify-between px-1" data-testid={`pitch-engagement-${pitch.id}`}>
        <div className="flex items-center gap-3">
          <button data-testid={`pitch-heart-btn-${pitch.id}`} onClick={() => engage("heart", "hearts")} className={`flex items-center gap-1 text-xs transition-colors ${reacted.heart ? "text-red-500" : "text-zinc-400 hover:text-red-500"}`}>
            <Heart size={14} fill={reacted.heart ? "currentColor" : "none"} /> {counts.hearts}
          </button>
          <button data-testid={`pitch-dollar-btn-${pitch.id}`} onClick={() => engage("dollar", "dollars")} className={`flex items-center gap-1 text-xs transition-colors ${reacted.dollar ? "text-[#F5B301]" : "text-zinc-400 hover:text-[#F5B301]"}`}>
            <CircleDollarSign size={14} /> {counts.dollars}
          </button>
          <button data-testid={`pitch-like-btn-${pitch.id}`} onClick={() => engage("like", "likes")} className={`flex items-center gap-1 text-xs transition-colors ${reacted.like ? "text-blue-400" : "text-zinc-400 hover:text-blue-400"}`}>
            <ThumbsUp size={14} fill={reacted.like ? "currentColor" : "none"} /> {counts.likes}
          </button>
        </div>
        <div className="flex items-center gap-3">
          {user && user.role === "investor" && (
            <button
              data-testid={`pitch-bookmark-btn-${pitch.id}`}
              onClick={onBookmark}
              aria-label={isSaved ? "Remove from watchlist" : "Save to watchlist"}
              className={`flex items-center gap-1 text-xs transition-colors ${isSaved ? "text-[#F5B301]" : "text-zinc-400 hover:text-[#F5B301]"}`}
            >
              <Bookmark size={14} fill={isSaved ? "currentColor" : "none"} />
            </button>
          )}
          <button data-testid={`pitch-share-btn-${pitch.id}`} onClick={share} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors">
            <Share2 size={13} /> Share
          </button>
        </div>
      </div>
    </div>
  );
};

export default PitchFlipCard;
